import { useCallback, useMemo, useState } from 'react';
import type { OverlayScrollbars } from 'overlayscrollbars';
import { useMarketDataContext } from '../../context/MarketDataContext.utils';
import { captureEvent } from '../../observability';
import { Scrollable } from '../primitives/Scrollable';
import styles from './FilterChips.module.css';

interface Props {
  compact?: boolean;
  /**
   * Fires after a chip is toggled. AppShell uses this to jump from the
   * Info tab back to the Vendors tab when a user engages with a filter —
   * otherwise the chips would have no visible effect on Info.
   */
  onChipInteract?: (() => void) | undefined;
}

export function FilterChips({ compact = false, onChipInteract }: Props) {
  const { state, showAllCategories, setCategoryFilter } = useMarketDataContext();
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  // Sub-pixel tolerance so the edge doesn't flicker on fractional scroll
  // positions (trackpads / high-DPI displays). With OverlayScrollbars, the
  // real scroll host is the .os-viewport nested inside the component wrapper,
  // so we read metrics off `instance.elements().viewport`.
  const syncFades = useCallback((instance: OverlayScrollbars) => {
    const viewport = instance.elements().viewport;
    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    setFadeLeft(viewport.scrollLeft > 0.5);
    setFadeRight(viewport.scrollLeft < maxScroll - 0.5);
  }, []);

  const allOn = useMemo(
    () => state.categories.every(c => state.filters.categoryIds.has(c.id)),
    [state.categories, state.filters.categoryIds],
  );

  // Render the (height-reserving) wrapper even before categories load so the
  // chip row appearing doesn't shift <main> down. The CSS min-height holds the
  // space; chips fill it once data arrives.
  if (state.categories.length === 0) {
    return <div className={`${styles.wrap} ${compact ? styles.wrapCompact : ''}`} aria-hidden="true" />;
  }

  function handleChipClick(id: string) {
    onChipInteract?.();
    const categoryName = state.categories.find((c) => c.id === id)?.name ?? id;
    if (allOn) {
      setCategoryFilter(new Set([id]));
      captureEvent('category_filter_changed', {
        category_id: id,
        category_name: categoryName,
        enabled: true,
        active_count: 1,
      });
      return;
    }
    const next = new Set(state.filters.categoryIds);
    const enabling = !next.has(id);
    if (next.has(id)) next.delete(id); else next.add(id);
    if (next.size === 0) {
      showAllCategories();
      captureEvent('category_filter_changed', {
        category_id: id,
        category_name: categoryName,
        enabled: false,
        active_count: state.categories.length,
      });
      return;
    }
    setCategoryFilter(next);
    captureEvent('category_filter_changed', {
      category_id: id,
      category_name: categoryName,
      enabled: enabling,
      active_count: next.size,
    });
  }

  function handleAllClick() {
    onChipInteract?.();
    showAllCategories();
  }

  const scrollerClass = [
    fadeLeft ? styles.scrollerFadeLeft : '',
    fadeRight ? styles.scrollerFadeRight : '',
  ].filter(Boolean).join(' ');

  const rowClass = [styles.row, compact ? styles.rowCompact : ''].filter(Boolean).join(' ');

  return (
    <div className={`${styles.wrap} ${compact ? styles.wrapCompact : ''}`}>
      <Scrollable
        axis="x"
        className={scrollerClass}
        events={{
          initialized: syncFades,
          updated: syncFades,
          scroll: syncFades,
        }}
      >
        <div className={rowClass} role="group" aria-label="Category filters">
          <button
            type="button"
            className={`${styles.chip} ${compact ? styles.chipCompact : ''} ${allOn ? styles.chipOn : ''}`}
            onClick={handleAllClick}
            aria-pressed={allOn}
          >
            All
          </button>
          {state.categories.map(cat => {
            const isOn = state.filters.categoryIds.has(cat.id);
            const isLit = isOn && !allOn;
            return (
              <button
                key={cat.id}
                type="button"
                className={`${styles.chip} ${compact ? styles.chipCompact : ''} ${isLit ? styles.chipOn : ''}`}
                onClick={() => handleChipClick(cat.id)}
                aria-pressed={isOn}
                data-cat-id={cat.id}
              >
                <span className={styles.dot} />
                {cat.name}
              </button>
            );
          })}
        </div>
      </Scrollable>
    </div>
  );
}
